import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { LocalizedLink } from '@/components/LocalizedLink'

export interface TwoColumnsBlockProps {
  chapter?: string
  eyebrow: string
  titleLine1: string
  titleLine2?: string
  body: string
  ctaLabel?: string
  ctaHref?: string
  /** Percorso interno per LocalizedLink (prende precedenza su ctaHref) */
  ctaTo?: string
  image: string
  imageAlt: string
  reverse?: boolean
  caption?: string
  bgTone?: 'cream' | 'cream-soft' | 'transparent'
}

export function TwoColumnsBlock({
  chapter,
  eyebrow,
  titleLine1,
  titleLine2,
  body,
  ctaLabel,
  ctaHref,
  ctaTo,
  image,
  imageAlt,
  reverse = false,
  caption,
  bgTone = 'cream',
}: TwoColumnsBlockProps) {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const yImage = useTransform(scrollYProgress, [0, 1], ['0%', '-12%'])
  const yText = useTransform(scrollYProgress, [0, 1], ['0%', '-4%'])

  const bgClass =
    bgTone === 'cream-soft'
      ? 'bg-cream-soft'
      : bgTone === 'transparent'
        ? 'bg-transparent'
        : 'bg-cream'

  return (
    <section ref={ref} className={`relative ${bgClass} py-24 md:py-36 overflow-hidden`}>
      {/* Decorative chapter mark */}
      {chapter && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.2 }}
          className="nashi-container mb-12 md:mb-20 flex items-baseline gap-4"
        >
          <span className="font-display italic text-argan-deep text-2xl md:text-3xl">
            {chapter}
          </span>
          <span className="block flex-1 h-px bg-bruno/15" />
          <span className="hidden md:block text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
            Capitolo
          </span>
        </motion.div>
      )}

      <div className="nashi-container">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
            reverse ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          {/* Image column */}
          <motion.div
            style={{ y: yImage }}
            className={`relative lg:col-span-7 ${
              reverse ? 'lg:col-start-6' : ''
            }`}
          >
            <div className="relative overflow-hidden bg-bruno/5 aspect-[4/5] md:aspect-[5/6]">
              <motion.img
                src={image}
                alt={imageAlt}
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              {/* Subtle gold corner accent */}
              <div className="pointer-events-none absolute top-0 left-0 w-12 h-12 border-t border-l border-cream/30" />
              <div className="pointer-events-none absolute bottom-0 right-0 w-12 h-12 border-b border-r border-cream/30" />
            </div>
            {caption && (
              <p className="mt-4 text-[10px] uppercase tracking-[0.28em] text-bruno-muted">
                <span className="text-argan-deep mr-2">—</span>
                {caption}
              </p>
            )}
          </motion.div>

          {/* Text column */}
          <motion.div
            style={{ y: yText }}
            className={`lg:col-span-5 ${
              reverse ? 'lg:col-start-1 lg:row-start-1 lg:pr-6' : 'lg:pl-6'
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
            >
              <span className="nashi-eyebrow">{eyebrow}</span>

              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2.25rem, 4.5vw, 4rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{titleLine1}</span>
                {titleLine2 && (
                  <span className="block italic font-light text-argan-deep">
                    {titleLine2}
                  </span>
                )}
              </h2>

              <p className="mt-8 text-bruno-soft text-base md:text-lg leading-relaxed max-w-md">
                {body}
              </p>

              {ctaLabel && (ctaTo || ctaHref) && (
                ctaTo ? (
                  <LocalizedLink
                    to={ctaTo}
                    className="group mt-10 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
                  >
                    {ctaLabel}
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                      →
                    </span>
                  </LocalizedLink>
                ) : (
                  <a
                    href={ctaHref}
                    className="group mt-10 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
                  >
                    {ctaLabel}
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                      →
                    </span>
                  </a>
                )
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
